import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Box,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Button
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { FormActions } from './ui';
import { useFormValidation } from '../hooks/useFormValidation';
import api from '../utils/api';

const ScheduledClassForm = ({ onSubmit, onCancel, initialData = null }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'tr' ? tr : enUS;

  const initialFormData = {
    classType: '',
    trainer: '',
    facility: '',
    startTime: '',
    duration: 1,
    capacity: '',
    basePrice: '',
    notes: '',
    sessionType: 'single',
    sessions: [],
    planAccess: [],
    facilityBooking: {
      type: 'partial-capacity'
    },
    recurringPattern: {
      type: 'weekly',
      frequency: 1,
      endDate: '',
      daysOfWeek: []
    }
  };

  const validationRules = {
    classType: [
      (value) => !value ? t('scheduledClass:validation.classTypeRequired') : null
    ],
    trainer: [
      (value) => !value ? t('scheduledClass:validation.trainerRequired') : null
    ],
    facility: [
      (value) => !value ? t('scheduledClass:validation.facilityRequired') : null
    ],
    startTime: [
      (value) => !value ? t('scheduledClass:validation.startTimeRequired') : null
    ],
    duration: [
      (value) => !value ? t('scheduledClass:validation.durationRequired') : null,
      (value) => value && (value < 0.5 || value > 8) ? t('scheduledClass:validation.durationRange') : null
    ],
    capacity: [
      (value) => {
        const capacity = parseInt(value);
        if (!value || isNaN(capacity) || capacity < 1) {
          return t('scheduledClass:validation.capacityRequired');
        }
        return null;
      }
    ],
    basePrice: [
      (value) => {
        const price = parseFloat(value);
        if (!value || isNaN(price) || price < 0) {
          return t('scheduledClass:validation.basePriceRequired');
        }
        if (price > 10000) {
          return t('scheduledClass:validation.basePriceMax');
        }
        return null;
      }
    ],
    planAccess: [
      // Plan access is required - classes must have at least one plan configuration
      (value) => !Array.isArray(value) ? 'Plan access must be an array' : null,
      (value) => Array.isArray(value) && value.length === 0 ? t('scheduledClass:validation.planAccessRequired') : null,
      (value) => {
        if (!Array.isArray(value) || value.length === 0) return null;

        // Check for incomplete configurations
        const incompletePlans = value.filter(pa => !pa.plan || pa.maxParticipants === undefined || pa.maxParticipants === null || pa.maxParticipants === '');
        if (incompletePlans.length > 0) {
          return t('scheduledClass:validation.planAccessIncomplete');
        }

        // Check for duplicate plans
        const planIds = value.map(pa => pa.plan).filter(Boolean);
        const uniquePlanIds = new Set(planIds);
        if (planIds.length !== uniquePlanIds.size) {
          return t('scheduledClass:validation.planAccessDuplicate');
        }

        return null;
      }
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    updateFields,
    handleSubmit,
    setFormData,
    getFieldError,
    hasFieldError,
    data
  } = useFormValidation(initialFormData, validationRules);

  // Helper function to update a specific session
  const updateSession = (sessionIndex, updates) => {
    updateFields({
      sessions: data.sessions.map((s, idx) =>
        idx === sessionIndex ? { ...s, ...updates } : s
      )
    });
  };

  const [options, setOptions] = useState({
    classTypes: [],
    trainers: [],
    facilities: [],
    plans: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [additionalErrors, setAdditionalErrors] = useState({});

  // Load form data if editing
  useEffect(() => {
    if (initialData) {
      // Improved date conversion with proper timezone handling
      let startTime = '';
      let calculatedDuration = 1;

      if (initialData.startTime) {
        try {
          const startDate = new Date(initialData.startTime);
          // Ensure we get the local datetime format for datetime-local input
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          const hours = String(startDate.getHours()).padStart(2, '0');
          const minutes = String(startDate.getMinutes()).padStart(2, '0');
          startTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (error) {
          console.error('Error converting startTime:', error);
          startTime = '';
        }
      }

      // Calculate duration from start and end times
      if (initialData.startTime && initialData.endTime) {
        try {
          const startDate = new Date(initialData.startTime);
          const endDate = new Date(initialData.endTime);
          const diffInMinutes = (endDate - startDate) / (1000 * 60);
          calculatedDuration = Math.max(0.5, diffInMinutes / 60); // Ensure minimum 0.5 hours
        } catch (error) {
          console.error('Error calculating duration:', error);
          calculatedDuration = 1;
        }
      }

      // Process sessions for editing - ensure proper structure
      let processedSessions = [];
      if (initialData.sessions && Array.isArray(initialData.sessions) && initialData.sessions.length > 0) {
        processedSessions = initialData.sessions.map((session, index) => ({
          _id: session._id || `temp_${Date.now()}_${index}`, // Keep existing MongoDB ID or create temp ID
          sessionNumber: session.sessionNumber || index + 1,
          title: session.title || '',
          description: session.description || '',
          duration: session.duration || 60, // Duration should already be in minutes from backend
          objectives: session.objectives || [],
          requiredEquipment: session.requiredEquipment || []
        }));
      }

      const formDataToSet = {
        classType: initialData.classType?._id || '',
        trainer: initialData.trainer?._id || '',
        facility: initialData.facility?._id || '',
        startTime,
        duration: calculatedDuration,
        capacity: initialData.capacity?.toString() || '',
        basePrice: initialData.basePrice?.toString() || '',
        notes: initialData.notes || '',
        sessionType: initialData.sessionType || 'single',
        sessions: processedSessions,
        planAccess: (initialData.planAccess && Array.isArray(initialData.planAccess) && initialData.planAccess.length > 0)
          ? initialData.planAccess.map(pa => ({
              ...pa,
              plan: pa.plan?._id || pa.plan, // Handle both populated object and string ID
              pricing: {
                type: pa.pricing?.type || 'fixed',
                value: pa.pricing?.value || 0,
                currency: pa.pricing?.currency || 'TRY'
              }
            }))
          : [],
        facilityBooking: {
          type: initialData.facilityBooking?.type || 'partial-capacity'
        },
        recurringPattern: {
          type: initialData.recurringPattern?.type || 'weekly',
          frequency: initialData.recurringPattern?.frequency || 1,
          endDate: initialData.recurringPattern?.endDate ?
            new Date(initialData.recurringPattern.endDate).toISOString().slice(0, 10) : '',
          daysOfWeek: initialData.recurringPattern?.daysOfWeek || []
        }
      };

      setFormData(formDataToSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Load options on component mount
  useEffect(() => {
    loadOptions();
  }, []);

  // Initialize plan access if editing and none exists
  useEffect(() => {
    if (initialData && options.plans.length > 0 && formData.planAccess.length === 0) {
      // If we're editing and no plan access exists, add a default configuration
      const basePrice = parseFloat(formData.basePrice) || 0;
      const defaultPlan = options.plans[0]; // Use the first available plan as default

      if (defaultPlan) {
        const newPlanConfig = {
          plan: defaultPlan._id,
          maxParticipants: parseInt(formData.capacity) || 10,
          currentParticipants: 0,
          requiresApproval: false,
          pricing: {
            type: 'fixed',
            value: basePrice,
            currency: 'TRY'
          }
        };

        updateField('planAccess', [newPlanConfig]);
      }
    }
  }, [initialData, options.plans, formData.planAccess.length, formData.basePrice, formData.capacity, updateField]);

  // Auto-set duration when class type changes
  useEffect(() => {
    if (formData.classType && !initialData) {
      const selectedClassType = options.classTypes.find(ct => ct._id === formData.classType);
      if (selectedClassType && selectedClassType.duration) {
        const durationInHours = selectedClassType.duration / 60;
        updateField('duration', durationInHours);
      }
    }
  }, [formData.classType, options.classTypes, updateField, initialData]);

  const loadOptions = async () => {
    try {
      setLoading(true);
      
      const [classTypesRes, trainersRes, facilitiesRes, plansRes] = await Promise.all([
        api.get('/class-types'),
        api.get('/users?role=trainer'),
        api.get('/facilities'),
        api.get('/plans')
      ]);

      setOptions({
        classTypes: classTypesRes.data.data || [],
        trainers: trainersRes.data.data || [],
        facilities: facilitiesRes.data.data || [],
        plans: plansRes.data.data || []
      });
    } catch (error) {
      console.error('Error loading form options:', error);
      setError('Failed to load form options. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateAdditionalFields = () => {
    const errors = {};

    // Validate start time is in the future (only for new classes, not when editing)
    if (formData.startTime && !initialData) {
      const startDate = new Date(formData.startTime);
      const now = new Date();
      if (startDate <= now) {
        errors.startTime = 'Start time must be in the future';
      }
    }

    // Duration validation is handled by validation rules

    // Validate capacity doesn't exceed facility capacity
    if (formData.facility && formData.capacity) {
      const selectedFacility = options.facilities.find(f => f._id === formData.facility);
      if (selectedFacility && parseInt(formData.capacity) > selectedFacility.capacity) {
        errors.capacity = t('scheduledClass:capacityError', { capacity: selectedFacility.capacity });
      }
    }

    // Validate multi-session configuration
    if (formData.sessionType === 'multi-session') {
      if (!formData.sessions || formData.sessions.length === 0) {
        errors.sessions = 'Multi-session classes must have at least one session';
      } else {
        // Validate each session has required fields
        const invalidSessions = formData.sessions.filter(session =>
          !session.title || !session.duration || session.duration < 15
        );
        if (invalidSessions.length > 0) {
          errors.sessions = 'All sessions must have a title and duration (minimum 15 minutes)';
        }
      }
    }

    // Validate plan access configuration
    if (formData.planAccess && formData.planAccess.length > 0) {
      const totalPlanCapacity = formData.planAccess.reduce((sum, pa) => sum + (pa.maxParticipants || 0), 0);
      if (totalPlanCapacity > parseInt(formData.capacity)) {
        errors.planAccess = t('scheduledClass:planCapacityError', { total: totalPlanCapacity, capacity: formData.capacity });
      }

      // Check for duplicate plans
      const planIds = formData.planAccess.map(pa => pa.plan).filter(Boolean);
      const uniquePlanIds = new Set(planIds);
      if (planIds.length !== uniquePlanIds.size) {
        errors.planAccess = 'Duplicate plans are not allowed';
      }

      // Check that all plan configurations are complete (only if plans are configured)
      if (formData.planAccess.length > 0) {
        const incompletePlans = formData.planAccess.filter(pa => !pa.plan || !pa.maxParticipants);
        if (incompletePlans.length > 0) {
          errors.planAccess = 'All plan access configurations must have a plan and max participants';
        }
      }
    }

    setAdditionalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFormSubmit = async (data) => {
    if (!validateAdditionalFields()) {
      throw new Error('Validation failed');
    }

    setError('');

    // Destructure to exclude recurringPattern and duration from the spread
    const { recurringPattern, duration, ...dataWithoutRecurringPattern } = data;

    // Calculate endTime from startTime + duration
    try {
      const startDate = new Date(data.startTime);
      if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date');
      }

      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + parseFloat(data.duration));
    } catch (error) {
      console.error('Date calculation error:', error);
      throw new Error('Invalid date or duration values');
    }

    // Parse datetime-local input and convert to proper ISO format
    // datetime-local format: "YYYY-MM-DDTHH:mm" (no timezone info)
    // Create a Date object from the local datetime string
    const startDate = new Date(data.startTime);
    const startTimeISO = startDate.toISOString();

    // Calculate end time by adding duration to start time
    const durationHours = parseFloat(data.duration);
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + durationHours);
    const endTimeISO = endDate.toISOString();

    // Clean up sessions data for multi-session classes
    let cleanedSessions = [];
    if (data.sessionType === 'multi-session' && data.sessions && data.sessions.length > 0) {
      cleanedSessions = data.sessions.map((session, index) => {
        // Remove temporary _id field if it starts with 'temp_'
        const { _id, ...sessionData } = session;
        const shouldIncludeId = _id && !String(_id).startsWith('temp_');

        return {
          ...(shouldIncludeId && { _id }), // Only include _id if it's a real MongoDB ID
          sessionNumber: session.sessionNumber || index + 1,
          title: session.title || '',
          description: session.description || '',
          duration: parseInt(session.duration) || 60,
          objectives: session.objectives || [],
          requiredEquipment: session.requiredEquipment || []
        };
      });
    }

    const submitData = {
      ...dataWithoutRecurringPattern,
      startTime: startTimeISO,
      endTime: endTimeISO,
      capacity: parseInt(data.capacity),
      basePrice: parseFloat(data.basePrice),
      sessions: cleanedSessions, // Use cleaned sessions
      facilityBooking: {
        ...data.facilityBooking,
        capacityReduction: parseInt(data.capacity)
      }
    };

    // Add recurring pattern ONLY if recurring type is selected
    if (data.sessionType === 'recurring' && data.recurringPattern) {
      submitData.isRecurring = true;

      submitData.recurringPattern = {
        frequency: data.recurringPattern.type, // "daily", "weekly", "monthly"
        interval: data.recurringPattern.frequency, // 1, 2, 3, etc.
        endDate: data.recurringPattern.endDate ?
          new Date(data.recurringPattern.endDate).toISOString() : null,
        daysOfWeek: data.recurringPattern.daysOfWeek
      };
    } else {
      // Explicitly set isRecurring to false for single sessions
      submitData.isRecurring = false;
      // Do NOT send recurringPattern for single sessions
    }

    try {
      await onSubmit(submitData);
    } catch (error) {
      // Display the error message from the parent component (already translated)
      setError(error.message);
      throw error; // Re-throw so the form knows submission failed
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) {
        console.warn('⚠️ Invalid date string:', dateTimeString);
        return 'Invalid Date';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('❌ Error formatting date:', error, 'Input:', dateTimeString);
      return 'Invalid Date';
    }
  };

  if (loading && options.classTypes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading form options...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Card>
        <CardHeader
          title={initialData ? t('scheduledClass:title.edit') : t('scheduledClass:title.create')}
          titleTypographyProps={{ variant: 'h4' }}
        />

        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
            {/* Class Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('scheduledClass:sections.classDetails')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('scheduledClass:sectionDescriptions.classDetails')}
              </Typography>
              
              <Grid container spacing={3} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={hasFieldError('classType')}>
                    <InputLabel>{t('scheduledClass:labels.classType')}</InputLabel>
                    <Select
                      value={formData.classType}
                      onChange={(e) => updateField('classType', e.target.value)}
                      label={t('scheduledClass:labels.classType')}
                    >
                      <MenuItem value="">{t('scheduledClass:placeholders.selectClassType')}</MenuItem>
                      {options.classTypes.map((classType) => (
                        <MenuItem key={classType._id} value={classType._id}>
                          {classType.name} ({classType.duration} min)
                        </MenuItem>
                      ))}
                    </Select>
                    {getFieldError('classType') && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {getFieldError('classType')}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={hasFieldError('trainer')}>
                    <InputLabel>{t('scheduledClass:labels.trainer')}</InputLabel>
                    <Select
                      value={formData.trainer}
                      onChange={(e) => updateField('trainer', e.target.value)}
                      label={t('scheduledClass:labels.trainer')}
                    >
                      <MenuItem value="">{t('scheduledClass:placeholders.selectTrainer')}</MenuItem>
                      {options.trainers.map((trainer) => (
                        <MenuItem key={trainer._id} value={trainer._id}>
                          {trainer.firstName} {trainer.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                    {getFieldError('trainer') && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {getFieldError('trainer')}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required error={hasFieldError('facility')}>
                    <InputLabel>{t('scheduledClass:labels.facility')}</InputLabel>
                    <Select
                      value={formData.facility}
                      onChange={(e) => updateField('facility', e.target.value)}
                      label={t('scheduledClass:labels.facility')}
                    >
                      <MenuItem value="">{t('scheduledClass:placeholders.selectFacility')}</MenuItem>
                      {options.facilities.map((facility) => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {t('scheduledClass:labels.facilityDisplay', { name: facility.name, type: facility.type, capacity: facility.capacity })}
                        </MenuItem>
                      ))}
                    </Select>
                    {getFieldError('facility') && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {getFieldError('facility')}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('scheduledClass:labels.capacity')}
                    required
                    value={formData.capacity}
                    onChange={(e) => updateField('capacity', e.target.value)}
                    placeholder="Enter capacity"
                    error={hasFieldError('capacity') || !!additionalErrors.capacity}
                    helperText={getFieldError('capacity') || additionalErrors.capacity || t('scheduledClass:helperTexts.capacity')}
                    inputProps={{ min: 1, max: 100 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('scheduledClass:labels.basePrice')}
                    required
                    value={formData.basePrice}
                    onChange={(e) => {
                      const newBasePrice = e.target.value;
                      updateField('basePrice', newBasePrice);
                      
                      // Auto-update fixed price plan access configurations
                      const basePriceNum = parseFloat(newBasePrice) || 0;
                      const updatedPlanAccess = formData.planAccess.map(planConfig => {
                        if (planConfig.pricing?.type === 'fixed') {
                          return {
                            ...planConfig,
                            pricing: {
                              ...planConfig.pricing,
                              value: basePriceNum
                            }
                          };
                        }
                        return planConfig;
                      });
                      
                      if (updatedPlanAccess.length > 0) {
                        updateField('planAccess', updatedPlanAccess);
                      }
                    }}
                    placeholder="Enter base price"
                    error={hasFieldError('basePrice')}
                    helperText={getFieldError('basePrice') || t('scheduledClass:helperTexts.basePrice')}
                    inputProps={{ min: 0, max: 10000, step: 0.01 }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Schedule Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('scheduledClass:sections.schedule')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('scheduledClass:sectionDescriptions.schedule')}
              </Typography>
              
              <Grid container spacing={3} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                    <DateTimePicker
                      label={t('scheduledClass:labels.startTime')}
                      value={formData.startTime ? new Date(formData.startTime) : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          // Convert Date object to datetime-local string format
                          const year = newValue.getFullYear();
                          const month = String(newValue.getMonth() + 1).padStart(2, '0');
                          const day = String(newValue.getDate()).padStart(2, '0');
                          const hours = String(newValue.getHours()).padStart(2, '0');
                          const minutes = String(newValue.getMinutes()).padStart(2, '0');
                          const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`;
                          updateField('startTime', dateTimeString);
                        } else {
                          updateField('startTime', '');
                        }
                      }}
                      minDateTime={!initialData ? new Date() : undefined}
                      ampm={false}
                      format="dd/MM/yyyy HH:mm"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: hasFieldError('startTime') || !!additionalErrors.startTime,
                          helperText: getFieldError('startTime') || additionalErrors.startTime || ''
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('scheduledClass:labels.duration')}
                    required
                    value={formData.duration}
                    onChange={(e) => updateField('duration', parseFloat(e.target.value) || '')}
                    error={hasFieldError('duration')}
                    helperText={
                      getFieldError('duration') ||
                      t('scheduledClass:helperTexts.duration')
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 
                      min: 0.5, 
                      max: 8, 
                      step: 0.5 
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Session Configuration Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('scheduledClass:sections.sessionConfiguration')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('scheduledClass:sectionDescriptions.sessionConfiguration')}
              </Typography>
              
              <Grid container spacing={3} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('scheduledClass:labels.sessionType')}</InputLabel>
                    <Select
                      value={formData.sessionType}
                      onChange={(e) => updateField('sessionType', e.target.value)}
                      label={t('scheduledClass:labels.sessionType')}
                    >
                      <MenuItem value="single">{t('scheduledClass:sessionTypes.single')}</MenuItem>
                      <MenuItem value="recurring">{t('scheduledClass:sessionTypes.recurring')}</MenuItem>
                      <MenuItem value="multi-session">{t('scheduledClass:sessionTypes.multiSession')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('scheduledClass:labels.facilityBookingType')}</InputLabel>
                    <Select
                      value={formData.facilityBooking.type}
                      onChange={(e) => updateFields({
                        facilityBooking: {
                          ...formData.facilityBooking,
                          type: e.target.value
                        }
                      })}
                      label={t('scheduledClass:labels.facilityBookingType')}
                    >
                      <MenuItem value="partial-capacity">{t('scheduledClass:facilityBookingTypes.partialCapacity')}</MenuItem>
                      <MenuItem value="whole-facility">{t('scheduledClass:facilityBookingTypes.wholeFacility')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Recurring Pattern Configuration */}
              {formData.sessionType === 'recurring' && (
                <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Recurring Pattern Configuration
                  </Typography>
                  
                  <Grid container spacing={3} sx={{ width: '100%' }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Repeat Type</InputLabel>
                        <Select
                          value={formData.recurringPattern.type}
                          onChange={(e) => updateFields({
                            recurringPattern: {
                              ...formData.recurringPattern,
                              type: e.target.value,
                              daysOfWeek: e.target.value === 'weekly' ? [] : formData.recurringPattern.daysOfWeek
                            }
                          })}
                          label="Repeat Type"
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Repeat Every"
                        value={formData.recurringPattern.frequency}
                        onChange={(e) => updateFields({
                          recurringPattern: {
                            ...formData.recurringPattern,
                            frequency: parseInt(e.target.value) || 1
                          }
                        })}
                        helperText={`Every ${formData.recurringPattern.frequency} ${formData.recurringPattern.type === 'daily' ? 'day(s)' : formData.recurringPattern.type === 'weekly' ? 'week(s)' : 'month(s)'}`}
                        inputProps={{ min: 1, max: 12 }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        type="date"
                        label="End Date"
                        value={formData.recurringPattern.endDate}
                        onChange={(e) => updateFields({
                          recurringPattern: {
                            ...formData.recurringPattern,
                            endDate: e.target.value
                          }
                        })}
                        InputLabelProps={{ shrink: true }}
                        helperText="When to stop recurring classes"
                      />
                    </Grid>

                    {formData.recurringPattern.type === 'weekly' && (
                      <Grid size={12}>
                        <Typography variant="body2" gutterBottom>
                          Select days of the week:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                            <FormControlLabel
                              key={day}
                              control={
                                <Checkbox
                                  checked={formData.recurringPattern.daysOfWeek.includes(day)}
                                  onChange={(e) => {
                                    const newDaysOfWeek = e.target.checked
                                      ? [...formData.recurringPattern.daysOfWeek, day]
                                      : formData.recurringPattern.daysOfWeek.filter(d => d !== day);
                                    updateFields({
                                      recurringPattern: {
                                        ...formData.recurringPattern,
                                        daysOfWeek: newDaysOfWeek
                                      }
                                    });
                                  }}
                                />
                              }
                              label={day}
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Multi-Session Configuration */}
              {formData.sessionType === 'multi-session' && (
                <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Multi-Session Course Configuration
                  </Typography>

                  {additionalErrors.sessions && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {additionalErrors.sessions}
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newSession = {
                          _id: `temp_${Date.now()}`, // Temporary ID for frontend tracking only
                          sessionNumber: formData.sessions.length + 1,
                          title: `Session ${formData.sessions.length + 1}`,
                          description: '',
                          duration: 60, // Duration in minutes (default 1 hour)
                          objectives: [],
                          requiredEquipment: []
                        };
                        updateField('sessions', [...formData.sessions, newSession]);
                      }}
                    >
                      Add Session
                    </Button>
                  </Box>

                  {formData.sessions.map((session, index) => (
                    <Box key={session._id || index} sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      backgroundColor: 'grey.50'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">Session {session.sessionNumber || index + 1}</Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newSessions = formData.sessions.filter((s, idx) => idx !== index);
                            // Renumber sessions after deletion
                            const renumberedSessions = newSessions.map((s, idx) => ({
                              ...s,
                              sessionNumber: idx + 1
                            }));
                            updateField('sessions', renumberedSessions);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            label="Session Title"
                            required
                            value={session.title || ''}
                            onChange={(e) => {
                              updateSession(index, { title: e.target.value });
                            }}
                            placeholder="e.g., Introduction to Yoga"
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Duration (minutes)"
                            required
                            value={session.duration || 60}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 60;
                              updateSession(index, { duration: newValue });
                            }}
                            slotProps={{
                              htmlInput: {
                                min: 15,
                                max: 240,
                                step: 15
                              }
                            }}
                            helperText="Duration in minutes (15-240)"
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Session Number"
                            disabled
                            value={session.sessionNumber || index + 1}
                            helperText="Auto-assigned based on order"
                          />
                        </Grid>

                        <Grid size={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Session Description"
                            value={session.description || ''}
                            onChange={(e) => {
                              updateSession(index, { description: e.target.value });
                            }}
                            placeholder="Describe what will be covered in this session"
                            slotProps={{
                              htmlInput: { maxLength: 1000 }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  
                  {formData.sessions.length === 0 && (
                    <Alert severity="info">
                      Click "Add Session" to create individual sessions for this course.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>

            {/* Plan Access Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('scheduledClass:sections.planAccess')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('scheduledClass:sectionDescriptions.planAccess')}
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>{t('scheduledClass:pricingGuide.title')}</strong>
                <br />• <strong>{t('scheduledClass:pricingGuide.fixedPrice')}</strong>
                <br />• <strong>{t('scheduledClass:pricingGuide.percentageOfBase')}</strong>
                <br />• <strong>{t('scheduledClass:pricingGuide.discount')}</strong>
              </Alert>
              
              {formData.planAccess.map((planConfig, index) => (
                <Box key={index} sx={{ 
                  border: '1px solid', 
                  borderColor: 'grey.300', 
                  borderRadius: 1, 
                  p: 2, 
                  mb: 2,
                  backgroundColor: 'grey.50'
                }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>{t('scheduledClass:labels.plan')}</InputLabel>
                        <Select
                          value={planConfig.plan}
                          onChange={(e) => {
                            const newPlanAccess = [...formData.planAccess];
                            newPlanAccess[index].plan = e.target.value;
                            updateField('planAccess', newPlanAccess);
                          }}
                          label={t('scheduledClass:labels.plan')}
                        >
                          {options.plans.map((plan) => (
                            <MenuItem key={plan._id} value={plan._id}>
                              {plan.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2.5 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('scheduledClass:labels.maxParticipants')}
                        value={planConfig.maxParticipants}
                        onChange={(e) => {
                          const newPlanAccess = [...formData.planAccess];
                          newPlanAccess[index].maxParticipants = parseInt(e.target.value) || 0;
                          updateField('planAccess', newPlanAccess);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2.5 }}>
                      <FormControl fullWidth>
                        <InputLabel>{t('scheduledClass:labels.pricingType')}</InputLabel>
                        <Select
                          value={planConfig.pricing?.type || 'fixed'}
                          onChange={(e) => {
                            const newPlanAccess = [...formData.planAccess];
                            const basePrice = parseFloat(formData.basePrice) || 0;

                            // When changing pricing type, set appropriate default value
                            let defaultValue = 0;
                            if (e.target.value === 'fixed') {
                              defaultValue = basePrice; // Use class base price for fixed pricing
                            } else if (e.target.value === 'percentage') {
                              defaultValue = 100; // 100% of base price
                            } else if (e.target.value === 'discount') {
                              defaultValue = 0; // No discount by default
                            }

                            newPlanAccess[index].pricing = {
                              ...newPlanAccess[index].pricing,
                              type: e.target.value,
                              value: defaultValue
                            };
                            updateField('planAccess', newPlanAccess);
                          }}
                          label={t('scheduledClass:labels.pricingType')}
                        >
                          <MenuItem value="fixed">{t('scheduledClass:pricingTypes.fixed')}</MenuItem>
                          <MenuItem value="percentage">{t('scheduledClass:pricingTypes.percentage')}</MenuItem>
                          <MenuItem value="discount">{t('scheduledClass:pricingTypes.discount')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2.5 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={
                          planConfig.pricing?.type === 'percentage' ? t('scheduledClass:labels.percentage') :
                          planConfig.pricing?.type === 'discount' ? t('scheduledClass:labels.discountAmount') :
                          planConfig.pricing?.type === 'fixed' ? t('scheduledClass:labels.price') : t('scheduledClass:labels.value')
                        }
                        value={planConfig.pricing?.value || 0}
                        onChange={(e) => {
                          const newPlanAccess = [...formData.planAccess];
                          newPlanAccess[index].pricing = {
                            ...newPlanAccess[index].pricing,
                            value: parseFloat(e.target.value) || 0
                          };
                          updateField('planAccess', newPlanAccess);
                        }}
                        inputProps={{
                          min: 0,
                          max: planConfig.pricing?.type === 'percentage' ? 100 : 10000,
                          step: 0.01
                        }}
                        placeholder={planConfig.pricing?.type === 'fixed' ? t('scheduledClass:placeholders.enterPrice') : '0'}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 1.5 }}>
                      <IconButton
                        onClick={() => {
                          const newPlanAccess = formData.planAccess.filter((_, i) => i !== index);
                          updateField('planAccess', newPlanAccess);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Box sx={{ mt: 2 }}>
                <IconButton
                  onClick={() => {
                    const basePrice = parseFloat(formData.basePrice) || 0;
                    const newPlanConfig = {
                      plan: '',
                      maxParticipants: 10,
                      pricing: {
                        type: 'fixed',
                        value: basePrice, // Use class base price as default
                        currency: 'TRY'
                      }
                    };
                    updateField('planAccess', [...formData.planAccess, newPlanConfig]);
                  }}
                  color="primary"
                  sx={{ 
                    border: '2px dashed', 
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 1,
                    width: '100%',
                    height: 60
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon />
                    <Typography variant="body2">{t('scheduledClass:buttons.addPlanAccess')}</Typography>
                  </Box>
                </IconButton>
              </Box>

              {(hasFieldError('planAccess') || additionalErrors.planAccess) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {getFieldError('planAccess') || additionalErrors.planAccess}
                </Typography>
              )}
            </Box>

            {/* Additional Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('scheduledClass:sections.additionalInformation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('scheduledClass:sectionDescriptions.additionalInformation')}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('scheduledClass:labels.notesOptional')}
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder={t('scheduledClass:placeholders.enterNotes')}
                helperText={t('scheduledClass:helperTexts.characterCount', { count: formData.notes.length })}
                inputProps={{ maxLength: 500 }}
              />
            </Box>

            {/* Form Actions */}
            <FormActions
              primaryAction={{
                label: initialData ? t('scheduledClass:buttons.update') : t('scheduledClass:buttons.save'),
                loading: isSubmitting,
                disabled: isSubmitting
              }}
              secondaryAction={{
                label: t('scheduledClass:buttons.cancel'),
                onClick: onCancel,
                disabled: isSubmitting
              }}
              alignment="right"
              variant="sticky"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScheduledClassForm;